import { useEffect, useState } from "react"

import { toast } from "sonner"

import { useDrbOptions, useUpdateDrbAssignment } from "@/hooks/useCollections"
import { indexDriversByPersonId } from "@/lib/routeAssignmentRows"
import { Route } from "@/types/collections"

/**
 * Each pick shows immediately and is written behind it; a failed write restores the previous value.
 */
export function useRouteAssignment(route: Route | null, enabled: boolean) {
  const { data: drbData, isLoading: isDrbLoading } = useDrbOptions(
    route?.code,
    enabled
  )
  const updateDrbAssignment = useUpdateDrbAssignment()

  const [drbDriverId, setDrbDriverId] = useState<string | null>(null)
  const [drbDriverName, setDrbDriverName] = useState<string | null>(null)
  const [drbVehicleId, setDrbVehicleId] = useState<string | null>(null)
  const [drbVehicleName, setDrbVehicleName] = useState<string | null>(null)
  const [drbTrailerId, setDrbTrailerId] = useState<string | null>(null)
  const [drbTrailerName, setDrbTrailerName] = useState<string | null>(null)
  // Airtable has no field for the second driver, so it's seeded from the route instance below.
  const [drbSecondDriverId, setDrbSecondDriverId] = useState<string | null>(null)
  const [drbSecondDriverName, setDrbSecondDriverName] = useState<string | null>(null)

  useEffect(() => {
    if (drbData?.currentAssignment) {
      setDrbDriverId(drbData.currentAssignment.driverId)
      setDrbDriverName(drbData.currentAssignment.driverName)
      setDrbVehicleId(drbData.currentAssignment.vehicleId)
      setDrbVehicleName(drbData.currentAssignment.vehicleName)
      setDrbTrailerId(drbData.currentAssignment.trailerId ?? null)
      setDrbTrailerName(drbData.currentAssignment.trailerName ?? null)
    } else {
      setDrbDriverId(null)
      setDrbDriverName(null)
      setDrbVehicleId(null)
      setDrbVehicleName(null)
      setDrbTrailerId(null)
      setDrbTrailerName(null)
    }
  }, [drbData?.currentAssignment, route?.code])

  /** Matched on person_id, not name: resourcing and the CRM spell names differently. */
  useEffect(() => {
    const personId = route?.secondDriverId?.toString().trim()
    if (!personId) {
      setDrbSecondDriverId(null)
      setDrbSecondDriverName(null)
      return
    }
    const option = indexDriversByPersonId(drbData?.drivers ?? []).get(personId)
    setDrbSecondDriverId(option?.id ?? null)
    // The stored label stands in until options land, so a route with a mate never reads
    // "Unassigned".
    setDrbSecondDriverName(option?.name ?? route?.secondDriverName ?? null)
  }, [route?.secondDriverId, route?.secondDriverName, drbData?.drivers])

  const handleDrbDriverChange = async (id: string | null, name: string | null) => {
    if (!route?.code) return
    setDrbDriverId(id)
    setDrbDriverName(name)

    try {
      await updateDrbAssignment.mutateAsync({
        routeCode: route.code,
        airtableRecordId: drbData?.currentAssignment?.airtableRecordId,
        routeInstanceId: route.id,
        driverId: id,
      })
      toast.success("Driver updated", {
        description: name
          ? `Assigned ${name} to ${route.code}`
          : `Unassigned driver from ${route.code}`,
      })
    } catch (err) {
      setDrbDriverId(drbData?.currentAssignment?.driverId ?? null)
      setDrbDriverName(drbData?.currentAssignment?.driverName ?? null)
      toast.error("Error updating driver", {
        description: err instanceof Error ? err.message : "Failed to update driver assignment",
      })
    }
  }

  // Reverts to the route instance, not the DRB record: that's the only place the previous value
  // exists.
  const handleDrbSecondDriverChange = async (id: string | null, name: string | null) => {
    if (!route?.code) return
    const previousId = drbSecondDriverId
    const previousName = drbSecondDriverName
    setDrbSecondDriverId(id)
    setDrbSecondDriverName(name)

    try {
      await updateDrbAssignment.mutateAsync({
        routeCode: route.code,
        airtableRecordId: drbData?.currentAssignment?.airtableRecordId,
        routeInstanceId: route.id,
        secondDriverId: id,
      })
      toast.success("Second driver updated", {
        description: name
          ? `Assigned ${name} as second driver on ${route.code}`
          : `Removed the second driver from ${route.code}`,
      })
    } catch (err) {
      setDrbSecondDriverId(previousId)
      setDrbSecondDriverName(previousName)
      toast.error("Error updating second driver", {
        description: err instanceof Error ? err.message : "Failed to update driver assignment",
      })
    }
  }

  const handleDrbVehicleChange = async (id: string | null, name: string | null) => {
    if (!route?.code) return
    setDrbVehicleId(id)
    setDrbVehicleName(name)

    try {
      await updateDrbAssignment.mutateAsync({
        routeCode: route.code,
        airtableRecordId: drbData?.currentAssignment?.airtableRecordId,
        routeInstanceId: route.id,
        vehicleId: id,
      })
      toast.success("Vehicle updated", {
        description: name
          ? `Assigned ${name} to ${route.code}`
          : `Unassigned vehicle from ${route.code}`,
      })
    } catch (err) {
      setDrbVehicleId(drbData?.currentAssignment?.vehicleId ?? null)
      setDrbVehicleName(drbData?.currentAssignment?.vehicleName ?? null)
      toast.error("Error updating vehicle", {
        description: err instanceof Error ? err.message : "Failed to update vehicle assignment",
      })
    }
  }

  // Airtable-only: no route instance column to mirror it into.
  const handleDrbTrailerChange = async (id: string | null, name: string | null) => {
    if (!route?.code) return
    setDrbTrailerId(id)
    setDrbTrailerName(name)

    try {
      await updateDrbAssignment.mutateAsync({
        routeCode: route.code,
        airtableRecordId: drbData?.currentAssignment?.airtableRecordId,
        routeInstanceId: route.id,
        trailerId: id,
      })
      toast.success("Trailer updated", {
        description: name
          ? `Assigned ${name} to ${route.code}`
          : `Unassigned trailer from ${route.code}`,
      })
    } catch (err) {
      setDrbTrailerId(drbData?.currentAssignment?.trailerId ?? null)
      setDrbTrailerName(drbData?.currentAssignment?.trailerName ?? null)
      toast.error("Error updating trailer", {
        description: err instanceof Error ? err.message : "Failed to update trailer assignment",
      })
    }
  }

  return {
    drbData,
    isDrbLoading,
    isSaving: updateDrbAssignment.isPending,
    driver: { id: drbDriverId, name: drbDriverName, onChange: handleDrbDriverChange },
    secondDriver: {
      id: drbSecondDriverId,
      name: drbSecondDriverName,
      onChange: handleDrbSecondDriverChange,
    },
    vehicle: { id: drbVehicleId, name: drbVehicleName, onChange: handleDrbVehicleChange },
    trailer: { id: drbTrailerId, name: drbTrailerName, onChange: handleDrbTrailerChange },
  }
}

export type RouteAssignment = ReturnType<typeof useRouteAssignment>
